import { alpha, type Theme, type ThemeOptions } from '@mui/material/styles';
import { ButtonProps as MuiButtonProps } from '@mui/material/Button';

const startEndIconSizing = {
  '& .MuiButton-startIcon, & .MuiButton-endIcon': {
    margin: 0,
    '& .MuiSvgIcon-root': {
      fontSize: 16,
      width: 16,
      height: 16
    }
  }
} as const;

const sizeSmallSizing = {
  height: 'auto',
  minHeight: 36,
  padding: '4px 10px',
  fontSize: '0.8125rem',
  lineHeight: 1.2
} as const;

const sizeMediumSizing = {
  height: 37,
  minHeight: 40,
  padding: '6px 14px'
} as const;

const sharedRoot = (size?: MuiButtonProps['size']) =>
  ({
    ...startEndIconSizing,
    ...(size === 'small' ? sizeSmallSizing : sizeMediumSizing),
    ...(size === 'small' && { '&.MuiButton-sizeSmall': sizeSmallSizing }),
    gap: 5,
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 500
  }) as const;

function variantRootStyles(theme: Theme, ownerState: { variant?: string; color?: string }) {
  if (ownerState.variant === 'contained') {
    const color = ownerState.color ?? 'primary';
    // MUI's default disabled contained button uses a dark (near-black) text color, which reads
    // as broken against our contained buttons' otherwise-white text — keep text white and only
    // dim the background instead.
    const disabledOverride = {
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.common.white
      }
    };
    if (color === 'primary') {
      return {
        fontWeight: 700,
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.primary.dark,
          boxShadow: 'none'
        },
        ...disabledOverride
      };
    }
    return {
      fontWeight: 700,
      '&:hover': {
        boxShadow: 'none'
      },
      ...disabledOverride
    };
  }
  if (ownerState.variant === 'outlined') {
    const color = ownerState.color ?? 'primary';
    if (color === 'primary') {
      return {
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.primary.main,
        '&:hover': {
          borderColor: theme.palette.primary.dark,
          color: theme.palette.primary.dark,
          backgroundColor: theme.palette.action.hover,
          boxShadow: 'none'
        }
      };
    }
    return {
      '&:hover': {
        boxShadow: 'none'
      }
    };
  }
  if (ownerState.variant === 'text') {
    const color = ownerState.color ?? 'primary';
    if (color === 'primary') {
      return {
        color: theme.palette.primary.main,
        '& .MuiTouchRipple-root': {
          display: 'none'
        },
        '&:hover': {
          color: theme.palette.primary.main,
          backgroundColor: 'transparent',
          textDecoration: 'underline'
        },
        '&:active': {
          color: theme.palette.primary.dark,
          backgroundColor: 'transparent',
          textDecoration: 'none'
        }
      };
    }
    return {
      '& .MuiTouchRipple-root': {
        display: 'none'
      },
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline'
      },
      '&:active': {
        backgroundColor: 'transparent',
        textDecoration: 'none'
      }
    };
  }
  return {};
}

export function buttonThemeComponents(theme: Theme): ThemeOptions['components'] {
  const neutralColor = theme.palette.grey[50];

  return {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...sharedRoot(ownerState.size),
          '&.SmButton-shape-square': {
            borderRadius: 10
          },
          '&.SmButton-align-spaceBetween': {
            justifyContent: 'flex-start',
            '& .MuiButton-endIcon': {
              marginInlineStart: 'auto'
            },
            // Text end adornment (e.g. a counter) — MUI sizes the adornment's first child as an icon (20px).
            '& .MuiButton-endIcon > *:not(.MuiSvgIcon-root)': {
              fontSize: theme.typography.pxToRem(12)
            }
          },
          ...variantRootStyles(theme, ownerState),
          '&.SmButton-color-neutral': {
            fontWeight: theme.typography.fontWeightBold,
            fontSize: theme.typography.pxToRem(14),
            '&.MuiButton-outlined': {
              border: `1.5px solid ${theme.palette.grey[200]}`,
              backgroundColor: theme.palette.common.white,
              color: theme.palette.common.black,
              '&:hover': {
                borderColor: theme.palette.grey[100],
                backgroundColor: theme.palette.grey[600],
                boxShadow: 'none'
              }
            },
            '&.MuiButton-contained': {
              backgroundColor: neutralColor,
              color: theme.palette.common.white,
              '&:hover': {
                backgroundColor: alpha(neutralColor, 0.85),
                boxShadow: 'none'
              }
            },
            '&.MuiButton-text': {
              color: theme.palette.common.black,
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline'
              }
            }
          }
        }),
        outlined: {
          fontWeight: 600
        },
        sizeSmall: sizeSmallSizing
      }
    }
  };
}
